/**
 * Per-champion identity color (#140) — resolves a champion's HUD/frame/nameplate
 * accent color, entirely client-side (presentation layer).
 *
 * WHY: team color (cyan vs red, `render/colors.ts`) tells you friend-vs-enemy but
 * not WHAT a unit is. In arena you identify a unit by its CLASS color at a glance
 * (peel for the healer, kick the caster). This adds a per-CHAMPION color, matching
 * the WoW class palette until unique classes emerge, so a Rogue reads yellow, a
 * Mage light-blue, a Priest white, a Warrior tan. Champion color is ADDITIVE:
 * team color still signals friend/enemy; champion color is the identity accent.
 *
 * BOUNDARY: champion color is cosmetic/presentation and MUST NOT touch the sim or
 * any hash. #617: the color KEY (`classColorKey`, a reference into `CLASS_COLORS`
 * below) now lives on the champion's content JSON
 * (`packages/content/data/champions/*.json`, `CHAMPION_SCHEMA`), but is explicitly
 * STRIPPED from the content hash (`@pit/content`'s `hash.ts`) — a recolor never
 * changes the sim/content hash, and a config-authored champion with no curated
 * `classColorKey` still works, just gets the deterministic fallback color below.
 * The actual hex PALETTE (`CLASS_COLORS`) stays client-only — `@pit/protocol` has
 * no rendering concern, it only pins the fixed set of valid class-archetype KEYS
 * a champion may reference (`CLASS_COLOR_KEY_SCHEMA`).
 *
 * #617 (was #419): previously this file hand-maintained a `championId → color`
 * table (`COLOR_BY_CHAMPION`) that 5 champions shipped alongside Warrior were
 * silently missing from. That table is gone — color now reads straight off the
 * loaded content catalog (`catalog.ts`'s `ALL_CONTENT_CHAMPIONS`, the same
 * glob-based build-time load already used for `id`/`slug`/`name`), so there is no
 * separate table a shipped champion's color can be forgotten from: it's either on
 * the champion's own content JSON, or it isn't (and falls back below).
 *
 * FALLBACK: an unmapped champion (test champ, or a config-authored one with no
 * `classColorKey` yet) gets a STABLE, distinct-ish color by hashing its id into
 * the class palette. The hash is pure + integer-only so the same id always
 * resolves to the same color (a champion never flickers between colors across
 * frames/reconnects).
 */

import type { ChampionId, ClassColorKey } from "@pit/protocol";
import { ALL_CONTENT_CHAMPIONS } from "./catalog.js";

/**
 * Canonical WoW class color palette (numeric = Pixi format, like
 * `render/colors.ts`). This is the source-of-truth palette; the per-champion
 * lookup below and the unmapped-fallback both draw from it. Values are the
 * canonical modern-WoW class hexes so anyone with muscle memory reads a unit
 * instantly. `satisfies Record<ClassColorKey, number>` keeps this object's key
 * set byte-identical to `@pit/protocol`'s `CLASS_COLOR_KEY_SCHEMA` enum — a
 * drift (adding a class here without adding it to the schema, or vice versa)
 * is a compile error, not a runtime surprise.
 */
export const CLASS_COLORS = {
  deathKnight: 0xc41e3a,
  demonHunter: 0xa330c9,
  druid: 0xff7c0a,
  evoker: 0x33937f,
  hunter: 0xaad372,
  mage: 0x3fc7eb,
  monk: 0x00ff98,
  paladin: 0xf48cba,
  priest: 0xffffff,
  rogue: 0xfff569,
  shaman: 0x0070dd,
  warlock: 0x8788ee,
  warrior: 0xc69b6d,
} as const satisfies Record<ClassColorKey, number>;

/**
 * #617: `championId → classColorKey`, built once from the loaded content
 * catalog (see this file's header doc). Replaces the old hand-maintained
 * `COLOR_BY_CHAMPION` table — a champion's curated color is either on its own
 * content JSON or it isn't; there's no second place to forget a row.
 */
const CLASS_COLOR_KEY_BY_CHAMPION: ReadonlyMap<ChampionId, ClassColorKey> = new Map(
  ALL_CONTENT_CHAMPIONS.filter(
    (c): c is typeof c & { classColorKey: ClassColorKey } => c.classColorKey !== undefined,
  ).map((c) => [c.id, c.classColorKey]),
);

/**
 * Neutral grey for the theoretically-impossible empty-palette case (keeps the
 * accessor total under `noUncheckedIndexedAccess`). Mirrors `UNKNOWN_TEAM_COLOR`
 * in `render/colors.ts` — defensive, not load-bearing.
 */
export const CHAMPION_COLOR_NEUTRAL = 0x888888;

/**
 * Palette slots the unmapped-champion fallback hashes into. Fixed canonical order
 * (declaration order of `CLASS_COLORS`) so the hash → color mapping is stable and
 * reviewable; reordering this array is a cosmetic reshuffle only.
 */
const FALLBACK_PALETTE: readonly number[] = Object.values(CLASS_COLORS);

/**
 * FNV-1a 32-bit string hash — pure and integer-only (`Math.imul` + `>>> 0` keep
 * it in 32-bit unsigned range, no floats). Used only to pick a stable fallback
 * palette slot for an unmapped champion; NOT part of any sim/content hash.
 */
function hashChampionId(championId: string): number {
  let h = 0x811c9dc5; // FNV offset basis
  for (let i = 0; i < championId.length; i++) {
    h ^= championId.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0; // FNV prime, wrap to uint32
  }
  return h >>> 0;
}

/**
 * The identity color for `championId`: its content-authored class color, or a
 * STABLE hashed-palette fallback for a champion with no curated `classColorKey`.
 * Always returns a usable color (never null) — callers tint directly. Pure lookup.
 */
export function championColor(championId: ChampionId): number {
  const key = CLASS_COLOR_KEY_BY_CHAMPION.get(championId);
  if (key !== undefined) return CLASS_COLORS[key];
  const idx = hashChampionId(championId) % FALLBACK_PALETTE.length;
  return FALLBACK_PALETTE[idx] ?? CHAMPION_COLOR_NEUTRAL;
}

/**
 * Test/introspection helper: the content-authored class color for `championId`, or
 * `null` if it has no curated `classColorKey` (→ hashed fallback). Mirrors `abilityIconKey`.
 */
export function mappedChampionColor(championId: ChampionId): number | null {
  const key = CLASS_COLOR_KEY_BY_CHAMPION.get(championId);
  return key !== undefined ? CLASS_COLORS[key] : null;
}

/**
 * `championColor` as a `#rrggbb` CSS string, for the DOM HUD frames (Pixi takes
 * the numeric form directly; the React frames need a CSS color). Pure.
 */
export function championColorCss(championId: ChampionId): string {
  return `#${championColor(championId).toString(16).padStart(6, "0")}`;
}
